import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import '../components/aiinterview/aiinterview.css';
import '../components/scheduling/scheduling.css';
import { errMsg } from '../lib/aiInterviewApi';
import { browserTz, candidateScheduleAPI, fmtSlot } from '../lib/schedulingApi';
import SlotPicker from '../components/scheduling/SlotPicker';
import TimezoneSelect from '../components/scheduling/TimezoneSelect';
import BookedCard from '../components/scheduling/BookedCard';

const ENDED = {
  cancelled: ['This interview was cancelled', 'If you still want to meet the team, reply to the email you received and they will help.'],
  expired: ['This scheduling link has expired', 'Please contact the recruiter for a new link.'],
  completed: ['This interview has taken place', 'Thanks for your time. The hiring team will be in touch.'],
  no_show: ['This interview time has passed', 'Please contact the recruiter if you would like to rebook.'],
};

const Shell = ({ ctx, children }) => (
  <div className="iv iv-page" data-testid="candidate-schedule-page">
    <header className="iv-top">
      <div className="iv-row" style={{ gap: '0.6rem' }}>
        <img src="/chire-icon.png" alt="CHIRE" width={28} height={28} style={{ borderRadius: 6 }} />
        <span style={{ fontWeight: 800, letterSpacing: '0.02em' }}>CHIRE <span style={{ fontWeight: 500, opacity: 0.7 }}>Scheduling</span></span>
      </div>
      {ctx?.company_name && <div className="iv-top-meta">{ctx.company_name}</div>}
    </header>
    <main className="iv-wrap">{children}</main>
  </div>
);

function Picker({ token, ctx, tz, onBooked }) {
  const [slots, setSlots] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => candidateScheduleAPI.slots(token).then(({ data }) => setSlots(data)).catch(() => setSlots([])), [token]);
  useEffect(() => { load(); }, [load]);
  const book = async () => {
    setBusy(true);
    try {
      await candidateScheduleAPI.book(token, { start: sel.start, timezone: tz });
      toast.success(ctx.status === 'scheduled' ? 'Your interview was moved' : 'Your interview is booked');
      onBooked();
    } catch (e) {
      toast.error(errMsg(e, 'Could not book that time'));
      setSel(null);
      load();
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="iv-card" data-testid="candidate-slot-section">
      <h2 className="iv-h2">{ctx.status === 'scheduled' ? 'Pick a new time' : 'Pick a time'}</h2>
      <SlotPicker slots={slots} tz={tz} value={sel?.start} onChange={setSel} emptyText="There are no open times right now. Please check back soon or reply to the invite email." />
      {sel && (
        <div className="iv-row" style={{ justifyContent: 'space-between', marginTop: '1rem', paddingTop: '0.9rem', borderTop: '1px solid #EEF2F5' }}>
          <span data-testid="candidate-selected-slot"><b style={{ color: '#002A38' }}>{fmtSlot(sel.start, tz)}</b> <span className="iv-small">· {ctx.duration_min} min</span></span>
          <button type="button" className="iv-btn iv-btn-cta" disabled={busy} onClick={book} data-testid="candidate-confirm-slot-btn">{busy ? 'Booking…' : 'Confirm this time'}</button>
        </div>
      )}
    </section>
  );
}

export default function CandidateSchedulePage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [missing, setMissing] = useState(false);
  const [tz, setTz] = useState(browserTz());
  const [picking, setPicking] = useState(false);
  const load = useCallback(() => candidateScheduleAPI.context(token).then(({ data }) => { setCtx(data); setPicking(false); })
    .catch((e) => (e.response?.status === 404 ? setMissing(true) : toast.error('Could not load this page'))), [token]);
  useEffect(() => { load(); }, [load]);

  if (missing) return <Shell><div className="iv-card" data-testid="schedule-invalid"><h1 className="iv-h1">This link isn't valid</h1><p className="iv-sub">Please check the link in your email, or contact the recruiter.</p></div></Shell>;
  if (!ctx) return <Shell><p className="iv-small">Loading…</p></Shell>;
  const ended = ENDED[ctx.status];
  return (
    <Shell ctx={ctx}>
      <div className="sc-hero" data-testid="schedule-hero">
        <div className="iv-eyebrow">{ctx.title}</div>
        <h1>{ctx.job_title}</h1>
        <p>Hi {ctx.candidate_first_name || 'there'}, {ctx.status === 'awaiting_candidate' ? 'choose a time that works for you.' : 'here are your interview details.'} {ctx.duration_min} minutes{ctx.company_name ? ` with ${ctx.company_name}` : ''}.</p>
      </div>
      {ended ? (
        <div className="iv-card" data-testid={`schedule-state-${ctx.status}`}><h2 className="iv-h2">{ended[0]}</h2><p className="iv-sub">{ended[1]}</p></div>
      ) : (
        <>
          <div className="iv-row" style={{ marginBottom: '0.75rem' }}>
            <label className="iv-small" htmlFor="cs-tz" style={{ fontWeight: 700 }}>Your timezone</label>
            <TimezoneSelect id="cs-tz" value={tz} onChange={setTz} testId="candidate-timezone-select" />
          </div>
          {ctx.notes && <div className="iv-note" style={{ marginBottom: '1rem' }}>{ctx.notes}</div>}
          {ctx.status === 'scheduled' && <BookedCard ctx={ctx} token={token} tz={tz} onReschedule={() => setPicking((p) => !p)} onChanged={load} />}
          {(ctx.status === 'awaiting_candidate' || picking) && <Picker token={token} ctx={ctx} tz={tz} onBooked={load} />}
        </>
      )}
    </Shell>
  );
}
